import * as React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Button, Chip, Stack, Typography } from "@mui/material";

import { usePreparationAction, type PreparationView } from "@/hooks/api/use-campaign-preparation";

import { AccountPageLink } from "./account-page-link";

export function ProvingResults({
	id,
	proof,
	approved,
	accountId,
}: {
	id: string;
	proof?: PreparationView["proving"];
	approved: boolean;
	accountId?: string | null;
}) {
	const action = usePreparationAction(id);
	if (!proof) return null;
	const active = ["queued", "running"].includes(proof.status);
	const labels = {
		not_started: "Waiting for admin approval",
		queued: "Tests queued",
		running: "Testing approved agents",
		passed: "All proving-ground tests passed",
		failed: "Tests need attention",
		stale: "Configuration changed; approve and test again",
	};
	return (
		<Stack spacing={2}>
			<Typography variant="h6">Proving Ground</Typography>
			<Alert severity={proof.status === "passed" ? "success" : proof.status === "failed" ? "warning" : "info"}>
				<strong>{labels[proof.status]}</strong>
				<div>
					{proof.passed} / {proof.total} conversations passed · ten voice tests per agent
				</div>
				{proof.error ? <div>{proof.error}</div> : null}
			</Alert>
			<Typography variant="body2" color="text.secondary">
				Approval starts ten simulated conversations with test personas and a simulated calendar. Campaign creation stays
				blocked until every required check passes. These tests do not dial the lead list or send invitations.
			</Typography>
			{proof.runs.map((run) => (
				<Accordion key={run.id}>
					<AccordionSummary>
						<Stack direction="row" spacing={2}>
							<Typography>
								Run #{run.id} · {run.completed}/{run.total} completed
							</Typography>
							<Chip size="small" label={run.status} />
						</Stack>
					</AccordionSummary>
					<AccordionDetails>
						<Stack spacing={2}>
							<AccountPageLink accountId={accountId} path={`/proving-ground/runs/${run.id}`}>
								Open recordings, transcripts and suggestions in Proving Ground
							</AccountPageLink>
							{run.scenarios.map((scenario) => (
								<Stack spacing={0.5} key={scenario.id}>
									<Typography variant="subtitle2">
										{scenario.passed ? "✓" : scenario.status === "scored" || scenario.status === "failed" ? "!" : "…"}{" "}
										{scenario.name}
									</Typography>
									{scenario.completionIssue ? (
										<Typography variant="body2" color="text.secondary">
											{scenario.completionIssue}
										</Typography>
									) : null}
									{scenario.checks
										.filter((check) => check.passed !== true || check.notApplicable)
										.map((check, index) => (
											<Typography variant="body2" color="text.secondary" key={index}>
												{check.name}: {check.reason || "Not scored"}
											</Typography>
										))}
								</Stack>
							))}
							{run.suggestions?.map((suggestion, index) => (
								<Alert severity="info" key={index}>
									<strong>{suggestion.title}</strong>
									<div>{suggestion.problem}</div>
								</Alert>
							))}
						</Stack>
					</AccordionDetails>
				</Accordion>
			))}
			{approved && !active ? (
				<Button
					sx={{ alignSelf: "start" }}
					variant="outlined"
					disabled={action.isPending}
					onClick={() => action.mutate({ action: "proving-retry" })}
				>
					Run ten tests again
				</Button>
			) : null}
			<Typography variant="body2" color="text.secondary">
				Review failures and suggested edits below or in Proving Ground. Prompt changes require fresh approval and a new
				test run. Go live remains a separate action.
			</Typography>
		</Stack>
	);
}
